import { useState } from "react";
import {
  AccessRequestPayload,
  RegistrationStatus,
  useRequestAccessMutation,
} from "../../api/authorization-api";
import Button from "../../components/Button";
import PageTitle from "../../components/PageTitle";
import styles from "./AccessRequestPage.module.css";

interface Props {
  status: RegistrationStatus;
}

function AccessRequestPage({ status }: Props) {
  const isPending = status === RegistrationStatus.PendingRegistration;

  const [form, setForm] = useState<AccessRequestPayload>({
    name: "",
    company: "",
    role: "",
    site: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const {
    mutate: requestAccess,
    isPending: isMutating,
    error,
  } = useRequestAccessMutation();

  function handleChange(field: keyof AccessRequestPayload, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setValidationError(null);
  }

  function handleSubmit() {
    if (!form.name.trim()) return setValidationError("Name is required.");
    if (!form.company.trim()) return setValidationError("Company is required.");
    if (!form.role.trim()) return setValidationError("Role is required.");
    if (!form.site.trim()) return setValidationError("Site is required.");

    requestAccess(form, {
      onSuccess: () => setSubmitted(true),
    });
  }

  const showPendingMessage = isPending || submitted;

  return (
    <div className={styles["access-request-page"]}>
      <div className={styles.card}>
        <PageTitle title="Request access" subtitle="" />

        {showPendingMessage ? (
          <p className={styles.message}>
            Your access request is pending approval. The ABB Connect team will
            process the request and contact you in case additional information
            is required.
          </p>
        ) : (
          <>
            <p className={styles.message}>
              Dear user, your account does not have any rights assigned,
              therefore you need to place a request. Once done, the ABB Connect
              team will process the request and contact you in case additional
              information is required. Please press the button below to place
              your request:
            </p>
            <div className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>
                  Name<span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.input}
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Your full name"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>
                  Company<span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.input}
                  value={form.company}
                  onChange={(e) => handleChange("company", e.target.value)}
                  placeholder="Your company"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>
                  Role<span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.input}
                  value={form.role}
                  onChange={(e) => handleChange("role", e.target.value)}
                  placeholder="Your role"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>
                  Site<span className={styles.required}>*</span>
                </label>
                <input
                  className={styles.input}
                  value={form.site}
                  onChange={(e) => handleChange("site", e.target.value)}
                  placeholder="Site you need access to"
                />
              </div>
              {validationError && (
                <p className={styles.error}>{validationError}</p>
              )}
              {error && (
                <p className={styles.error}>
                  Failed to submit request. Please try again.
                </p>
              )}
              <div className={styles.actions}>
                <Button
                  size="small"
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isMutating}
                >
                  {isMutating ? "Submitting…" : "Request Access"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AccessRequestPage;
